import React, { useState, useMemo, useEffect } from "react"
import { createPortal } from "react-dom"
import { Menu, MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faEdit,
  faSync,
  faChartColumn,
  faChartBar,
  faCheck,
  faList,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"

import useContextMenu from "features/Reporting/hooks/UI/useContextMenu"
import { TransformedTableData } from "utils/convertDataToTable"
import {
  createMenuItem,
  createCheckMenuItem,
  createIconCheckMenuItem,
} from "utils/contextMenuHelpers"

import { CHART_DISPLAY_TYPE } from "types/ReportingDashboard/elements.types"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"

/**
 * Chart type option interface
 */
interface ChartTypeOption {
  value: CHART_DISPLAY_TYPE
  label: string
  icon: IconDefinition
}

/**
 * Chart type options constant
 */
const CHART_TYPE_OPTIONS: ChartTypeOption[] = [
  { value: CHART_DISPLAY_TYPE.BAR, label: "Bar Chart", icon: faChartColumn },
  {
    value: CHART_DISPLAY_TYPE.HORIZONTAL_BAR,
    label: "Horizontal Bar",
    icon: faChartBar,
  },
]

/**
 * Submenu component props
 */
interface SubmenuProps {
  show: boolean
  items: MenuProps["items"]
  className: string
  style?: React.CSSProperties
}

/**
 * Props for ChartElementContextMenu component
 */
export interface ChartElementContextMenuProps {
  /** Whether chart data exists */
  hasChartData: boolean
  /** Whether component is in selection mode */
  showSelects: boolean
  /** Whether component is loading */
  isLoading: boolean
  /** Whether component is queued for execution */
  isQueuedForExecution: boolean
  /** Whether in dashboard mode */
  isDashboard: boolean
  /** Converted chart data */
  convertedChartData: TransformedTableData | null
  /** Current chart type */
  chartType: CHART_DISPLAY_TYPE
  /** Whether to show totals */
  showTotals: boolean
  /** Whether data is simple (single key) */
  isSimpleData: boolean
  /** Selected dashboard keys */
  selectedDashboardKeys: string[]
  /** Selected dashboard categories */
  selectedDashboardCategories: string[]
  /** Whether delete is available */
  canDelete: boolean
  /** Refresh handler */
  onRefresh: () => void
  /** Edit handler */
  onEdit: () => void
  /** Delete handler */
  onDelete?: () => void
  /** Toggle totals handler */
  onToggleTotals: () => void
  /** Chart type change handler */
  onChartTypeChange: (type: CHART_DISPLAY_TYPE) => void
  /** Variable toggle handler */
  onVariableToggle: (key: string) => void
  /** Category toggle handler */
  onCategoryToggle: (category: string) => void
}

/**
 * Chart Context Menu Component
 *
 * Handles the context menu and submenu logic including:
 * - Main context menu items
 * - Chart type submenu
 * - Variables submenu (for dashboard mode)
 * - Categories submenu (for dashboard mode)
 * - Click-outside handling for submenus
 *
 * Returns both the handleContextMenu function and the rendered menus.
 */
const useChartElementContextMenu = ({
  hasChartData,
  showSelects,
  isLoading,
  isQueuedForExecution,
  isDashboard,
  convertedChartData,
  chartType,
  showTotals,
  isSimpleData,
  selectedDashboardKeys,
  selectedDashboardCategories,
  canDelete,
  onRefresh,
  onEdit,
  onDelete,
  onToggleTotals,
  onChartTypeChange,
  onVariableToggle,
  onCategoryToggle,
}: ChartElementContextMenuProps) => {
  // Submenu state
  const [showVariablesMenu, setShowVariablesMenu] = useState<boolean>(false)
  const [showCategoriesMenu, setShowCategoriesMenu] = useState<boolean>(false)
  const [layoutMenuVisible, setLayoutMenuVisible] = useState<boolean>(false)

  // Helper functions
  const closeSubmenus = (): void => {
    setShowVariablesMenu(false)
    setShowCategoriesMenu(false)
    setLayoutMenuVisible(false)
  }

  // Context menu items
  const contextMenuItems = useMemo((): MenuProps["items"] => {
    const items: MenuProps["items"] = []

    if (!showSelects) {
      items.push(
        createMenuItem("refresh", faSync, "Refresh Chart", onRefresh, {
          disabled: isLoading || isQueuedForExecution,
        }),
        createMenuItem("edit", faEdit, "Edit Variables", onEdit),
        createMenuItem(
          "chartType",
          chartType === CHART_DISPLAY_TYPE.BAR ? faChartColumn : faChartBar,
          "Chart Type",
          () => setLayoutMenuVisible(true)
        )
      )

      if (
        !isSimpleData ||
        (convertedChartData &&
          convertedChartData.barChartData.keys[0] === "Totals")
      ) {
        items.push({
          key: "toggleTotals",
          icon: (
            <FontAwesomeIcon
              icon={faCheck}
              style={{ visibility: showTotals ? "visible" : "hidden" }}
            />
          ),
          label: "Show Totals",
          onClick: onToggleTotals,
        })
      }

      if (isDashboard && hasChartData && !isSimpleData) {
        items.push(
          createMenuItem("variables", faChartColumn, "Variables", () =>
            setShowVariablesMenu(true)
          ),
          createMenuItem("categories", faList, "Categories", () =>
            setShowCategoriesMenu(true)
          )
        )
      }
    } else {
      items.push(createMenuItem("edit", faEdit, "Reset Variables", onEdit))
    }

    if (canDelete) {
      items.push(
        { type: "divider", key: "div-delete" },
        createMenuItem("delete", faTrash, "Delete Chart", onDelete!, {
          danger: true,
        })
      )
    }

    return items
  }, [
    showSelects,
    isLoading,
    isQueuedForExecution,
    isDashboard,
    hasChartData,
    isSimpleData,
    convertedChartData,
    chartType,
    showTotals,
    canDelete,
    onRefresh,
    onEdit,
    onToggleTotals,
    onDelete,
  ])

  const chartTypeMenuItems = useMemo(
    (): MenuProps["items"] =>
      CHART_TYPE_OPTIONS.map((option) =>
        createIconCheckMenuItem(
          `chartType-${option.value}`,
          option.icon,
          option.label,
          chartType === option.value,
          () => onChartTypeChange(option.value)
        )
      ),
    [chartType, onChartTypeChange]
  )

  const variableMenuItems = useMemo((): MenuProps["items"] => {
    if (!isDashboard || !convertedChartData || isSimpleData) return []
    return convertedChartData.barChartData.keys
      .filter((k) => k !== "Totals")
      .map((k) =>
        createCheckMenuItem(
          `var-${k}`,
          k,
          selectedDashboardKeys.includes(k),
          () => onVariableToggle(k)
        )
      )
  }, [
    isDashboard,
    convertedChartData,
    isSimpleData,
    selectedDashboardKeys,
    onVariableToggle,
  ])

  const categoryMenuItems = useMemo((): MenuProps["items"] => {
    if (!isDashboard || !convertedChartData) return []
    return convertedChartData.barChartData.data
      .filter((i) => i.xValue !== "Totals")
      .map((i) =>
        createCheckMenuItem(
          `cat-${i.xValue}`,
          String(i.xValue),
          selectedDashboardCategories.includes(String(i.xValue)),
          () => onCategoryToggle(String(i.xValue))
        )
      )
  }, [
    isDashboard,
    convertedChartData,
    selectedDashboardCategories,
    onCategoryToggle,
  ])

  // Context menu hook
  const { handleContextMenu, position, isVisible, ContextMenu } =
    useContextMenu({
      menuItems: contextMenuItems,
      isEnabled: !!(
        (!showSelects && hasChartData) ||
        (showSelects && canDelete)
      ),
      additionalCloseSelectors: [
        ".variables-submenu",
        ".categories-submenu",
        ".layout-submenu",
      ],
      baseClassName: "chart-context-menu",
    })

  // Submenu component
  const Submenu: React.FC<SubmenuProps> = ({
    show,
    items,
    className,
    style = {},
  }) => {
    if (!show || !isVisible) return null

    // Calculate submenu position relative to viewport (since main menu is now portaled)
    const submenuElement = (
      <div
        className={`${className} chart-context-menu`}
        style={{
          position: "fixed",
          top: position.y,
          left: position.x + 160, // Offset from main menu position
          zIndex: 1001, // Higher than main menu
          backgroundColor: "white",
          border: "1px solid #ddd",
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          minWidth: "180px",
          ...style,
        }}
      >
        <Menu items={items} />
      </div>
    )

    // Render submenu as portal too, to match main menu behavior
    return createPortal(submenuElement, document.body)
  }

  // Handle submenu click-outside
  useEffect(() => {
    const handleClickOutside = (e: Event): void => {
      const target = e.target as Element
      if (
        (showVariablesMenu || showCategoriesMenu || layoutMenuVisible) &&
        !target.closest(".variables-submenu") &&
        !target.closest(".categories-submenu") &&
        !target.closest(".layout-submenu") &&
        !target.closest(".chart-context-menu")
      ) {
        closeSubmenus()
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [showVariablesMenu, showCategoriesMenu, layoutMenuVisible])

  const ContextMenus = (
    <>
      {/* Context menu and submenus */}
      {ContextMenu}
      <Submenu
        show={showVariablesMenu}
        items={variableMenuItems}
        className="variables-submenu"
      />
      <Submenu
        show={showCategoriesMenu}
        items={categoryMenuItems}
        className="categories-submenu"
        style={{ maxHeight: "300px", overflowY: "auto" }}
      />
      <Submenu
        show={layoutMenuVisible}
        items={chartTypeMenuItems}
        className="layout-submenu"
        style={{ minWidth: "200px" }}
      />
    </>
  )

  return {
    handleContextMenu,
    ContextMenus,
  }
}

/**
 * Component wrapper that uses the hook and returns React elements
 */
const ChartElementContextMenu: React.FC<ChartElementContextMenuProps> = (
  props
) => {
  const { ContextMenus } = useChartElementContextMenu(props)
  return <>{ContextMenus}</>
}

// Export the hook for direct use
export { useChartElementContextMenu }

export default ChartElementContextMenu
